import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { User, MapPin, Calendar, Image as ImageIcon, Ticket, FileText, Shield, XCircle, HelpCircle, MessageSquare, Phone, LogOut, ChevronRight, Check } from 'lucide-react';

export default function Profile() {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    date_of_birth: '',
    gender: '1',
  });
  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showEditMobile, setShowEditMobile] = useState(false);
  
  const navigate = useNavigate();

  const loadProfileUser = async () => {
    const userDataStr = localStorage.getItem('user_data');
    if (!userDataStr) {
      setUser(null);
      return;
    }
    const userData = JSON.parse(userDataStr);
    setUser(userData);
    
    setFormData({
      name: userData.name || '',
      address: userData.address || '',
      date_of_birth: userData.date_of_birth || '',
      gender: userData.gender || '1',
    });

    try {
      const response = await fetch(`/api/get_user_details/${userData.id}`, {
        headers: {
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123'
        }
      });
      if (response.ok) {
        const result = await response.json();
        const latestData = result.data || (Array.isArray(result) ? result[0] : result);
        if (latestData && latestData.id) {
          const updatedUser = { ...userData, ...latestData };
          localStorage.setItem('user_data', JSON.stringify(updatedUser));
          setUser(updatedUser);
          
          setFormData({
            name: updatedUser.name || '',
            address: updatedUser.address || '',
            date_of_birth: updatedUser.date_of_birth || '',
            gender: updatedUser.gender || '1',
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch user data', err);
    }
  };

  useEffect(() => {
    loadProfileUser();
    window.addEventListener('auth_changed', loadProfileUser);
    return () => window.removeEventListener('auth_changed', loadProfileUser);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setProfileImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const data = new FormData();
      data.append('name', formData.name);
      data.append('address', formData.address);
      data.append('date_of_birth', formData.date_of_birth);
      data.append('gender', formData.gender);
      data.append('user_id', user.id);
      
      if (profileImage) {
        data.append('profile_image', profileImage);
      }

      const response = await fetch('/api/update_profile', {
        method: 'POST',
        headers: {
          'Client-ID': 'lokanatya_client',
          'Client-Secret': 'super_secret_key_123'
        },
        body: data
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const result = await response.json();
      
      // Update local storage if backend returns updated user data
      if (result && result.data) {
        const updatedUser = { ...user, ...result.data };
        localStorage.setItem('user_data', JSON.stringify(updatedUser));
        setUser(updatedUser);
        window.dispatchEvent(new Event('auth_changed'));
      }

      setSuccessMsg('Profile updated successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setErrorMsg(err.message || 'An error occurred while updating profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user_data');
    localStorage.removeItem('auth_token');
    window.dispatchEvent(new Event('auth_changed'));
    navigate('/');
  };

  if (!user) {
    return (
      <div className="min-h-[70vh] bg-[#fcfcfc] flex flex-col items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-sm text-center max-w-md w-full border border-gray-100">
          <div className="text-gray-300 flex justify-center mb-4">
            <User size={64} />
          </div>
          <h2 className="text-xl md:text-2xl font-black text-gray-900 mb-2">Sign In Required</h2>
          <p className="text-gray-500 mb-6">Please log in to view your profile.</p>
          <button 
             onClick={() => window.dispatchEvent(new Event('openLoginModal'))}
             className="w-full bg-[#d33a33] text-white font-bold py-3 rounded-xl hover:bg-red-700 transition"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  const menuItems = [
    { icon: <Ticket size={20} />, label: 'My Tickets', link: '/my-tickets' },
    { icon: <FileText size={20} />, label: 'Terms & Conditions', link: '#' },
    { icon: <Shield size={20} />, label: 'Privacy Policy', link: '#' },
    { icon: <XCircle size={20} />, label: 'Cancel & Refund', link: '#' },
    { icon: <HelpCircle size={20} />, label: 'Help & Support', link: '#' },
    { icon: <MessageSquare size={20} />, label: 'Message Us', link: '#' },
    { icon: <Phone size={20} />, label: 'Call Us', link: '#' },
  ];

  return (
    <div className="bg-[#fcfcfc] min-h-screen py-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="flex flex-col md:flex-row gap-8">
          
          {/* Sidebar Menu */}
          <div className={`w-full md:w-1/3 lg:w-1/4 ${showEditMobile ? 'hidden md:block' : 'block'}`}>
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden sticky top-24 pb-4">
              
              {/* New Profile Header */}
              <div className="p-8 bg-gray-50 flex flex-col items-center relative overflow-hidden rounded-t-3xl border-b border-gray-100">
                {/* Decorative background circles */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-100/50 rounded-full translate-x-1/3 -translate-y-1/4 pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gray-200/50 rounded-full -translate-x-1/2 translate-y-1/4 pointer-events-none"></div>
                
                <div className="relative w-24 h-24 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm border-2 border-red-500/30 p-1 z-10">
                  <div className="w-full h-full rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                    {user.profile_image ? (
                      <img src={user.profile_image} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <User size={40} className="text-gray-400" />
                    )}
                  </div>
                </div>
                <h3 className="font-extrabold text-xl md:text-2xl text-gray-900 z-10">{user.name || 'Your Name'}</h3>
                <p className="text-gray-500 font-medium mb-4 z-10">{user.mobile}</p>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest z-10">Lokanatya Account</p>
              </div>
              
              <div className="p-4 bg-white">
                {/* ACCOUNT Section */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3 px-2">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Account</span>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {/* Item 1 */}
                    <div 
                      onClick={() => setShowEditMobile(true)}
                      className="flex items-center justify-between p-3 border border-gray-100 rounded-2xl cursor-pointer hover:border-red-200 hover:shadow-sm transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center text-red-600 group-hover:bg-red-500 group-hover:text-white transition-colors">
                          <User size={20} />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 text-sm">Edit Profile</h4>
                          <p className="text-xs text-gray-500">Update name & email</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-red-400 transition-colors" />
                    </div>

                    {/* Item 2 */}
                    <Link to="/my-tickets" className="flex items-center justify-between p-3 border border-gray-100 rounded-2xl cursor-pointer hover:border-red-200 hover:shadow-sm transition-all group">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center text-red-600 group-hover:bg-red-500 group-hover:text-white transition-colors">
                          <Ticket size={20} />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 text-sm">My Tickets</h4>
                          <p className="text-xs text-gray-500">View all bookings</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-red-400 transition-colors" />
                    </Link>
                  </div>
                </div>

                {/* APP Section */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-3 px-2">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Legal & Policies</span>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    {/* Terms & Conditions */}
                    <Link to="/terms-conditions" className="flex items-center justify-between p-3 border border-gray-100 rounded-2xl cursor-pointer hover:border-red-200 hover:shadow-sm transition-all group">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center text-red-600 group-hover:bg-red-500 group-hover:text-white transition-colors">
                          <FileText size={20} />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 text-sm">Terms & Conditions</h4>
                          <p className="text-xs text-gray-500">Payment & booking rules</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-red-400 transition-colors" />
                    </Link>

                    {/* Privacy Policy */}
                    <Link to="/privacy-policy" className="flex items-center justify-between p-3 border border-gray-100 rounded-2xl cursor-pointer hover:border-red-200 hover:shadow-sm transition-all group">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center text-red-600 group-hover:bg-red-500 group-hover:text-white transition-colors">
                          <Shield size={20} />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 text-sm">Privacy Policy</h4>
                          <p className="text-xs text-gray-500">Data protection & security</p>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-red-400 transition-colors" />
                    </Link>
                  </div>
                </div>

                <div className="h-px bg-gray-100 my-6 mx-3"></div>
                
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 p-3 text-red-600 hover:bg-red-50 rounded-xl transition font-bold"
                >
                  <LogOut size={18} /> Logout
                </button>
              </div>
            </div>
          </div>
          
          {/* Main Content - Edit Profile */}
          <div className={`w-full md:w-2/3 lg:w-3/4 ${showEditMobile ? 'block' : 'hidden md:block'}`}>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <button onClick={() => setShowEditMobile(false)} className="md:hidden mr-2 p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition">
                  <ChevronRight className="rotate-180" size={20} />
                </button>
                Edit Profile
              </h2>
              
              {successMsg && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl flex items-center gap-3">
                  <Check size={20} /> {successMsg}
                </div>
              )}
              
              {errorMsg && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
                  {errorMsg}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Full Name</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User size={18} className="text-gray-400" />
                      </div>
                      <input 
                        type="text" 
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition outline-none"
                        placeholder="e.g. Kaibalya Mohapatra"
                      />
                    </div>
                  </div>
                  
                  {/* Date of Birth */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Date of Birth</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Calendar size={18} className="text-gray-400" />
                      </div>
                      <input 
                        type="date" 
                        name="date_of_birth"
                        value={formData.date_of_birth}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition outline-none"
                      />
                    </div>
                  </div>
                  
                  {/* Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-bold text-gray-700 mb-2">Address</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 pt-3 pointer-events-none">
                        <MapPin size={18} className="text-gray-400" />
                      </div>
                      <textarea 
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        rows="3"
                        className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition outline-none"
                        placeholder="e.g. Paradeep"
                      ></textarea>
                    </div>
                  </div>
                  
                  {/* Gender */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Gender</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="gender" 
                          value="1" 
                          checked={formData.gender === '1'} 
                          onChange={handleChange}
                          className="w-4 h-4 text-red-500 focus:ring-red-500" 
                        />
                        <span>Male</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="gender" 
                          value="2" 
                          checked={formData.gender === '2'} 
                          onChange={handleChange}
                          className="w-4 h-4 text-red-500 focus:ring-red-500" 
                        />
                        <span>Female</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Profile Image */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Profile Image</label>
                    <div className="relative">
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={handleImageChange}
                        className="w-full p-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-bold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                      />
                    </div>
                  </div>
                  
                </div>
                
                <div className="pt-4 flex justify-end">
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full transition shadow-md disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
                
              </form>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}
